import { useState, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useAuth } from '../contexts/AuthContext'
import { useNotification } from '../contexts/NotificationContext'
import { uploadedInvoiceService } from '../services/uploadedInvoiceService'
import type { UploadInvoiceData, PlanLimits } from '../types/invoice'
import {
  XMarkIcon,
  CloudArrowUpIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  CurrencyDollarIcon,
  CalendarIcon,
  UserIcon
} from '@heroicons/react/24/outline'

interface InvoiceUploadModalProps {
  isOpen: boolean
  onClose: () => void
  onUploadSuccess: () => void
  planLimits: PlanLimits | null
}

const InvoiceUploadModal = ({ isOpen, onClose, onUploadSuccess, planLimits }: InvoiceUploadModalProps) => {
  const { user } = useAuth()
  const { showSuccess, showError } = useNotification()
  const fileInputRef = useRef<HTMLInputElement>(null)
  
  const [isUploading, setIsUploading] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [dragActive, setDragActive] = useState(false)
  
  const [formData, setFormData] = useState({
    title: '',
    clientName: '',
    amount: '',
    issueDate: '',
    dueDate: '',
    notes: ''
  })

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const handleFileSelect = (file: File) => {
    // Validate file type
    const allowedTypes = [
      'application/pdf',
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/gif',
      'image/webp'
    ]
    
    if (!allowedTypes.includes(file.type)) {
      showError('Invalid File Type', 'Please upload PDF or image files only.')
      return
    }

    // Validate file size (50MB)
    if (file.size > 52428800) {
      showError('File Too Large', 'File size must be less than 50MB.')
      return
    }

    setSelectedFile(file)
    
    // Auto-populate title from filename if not already set
    if (!formData.title) {
      const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '')
      setFormData(prev => ({ ...prev, title: nameWithoutExt }))
    }
  }

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      handleFileSelect(file)
    }
  }

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    
    const file = e.dataTransfer.files?.[0]
    if (file) {
      handleFileSelect(file)
    }
  }

  const handleUpload = async () => {
    if (!user || !selectedFile) return

    // Check plan limits
    if (planLimits && !planLimits.canUploadInvoice) {
      showError(
        'Upload Limit Reached',
        `You've reached your limit of ${planLimits.maxUploadedInvoices} uploaded invoices. Upgrade to Professional for unlimited uploads.`
      )
      return
    }

    setIsUploading(true)

    try {
      const uploadData: UploadInvoiceData = {
        file: selectedFile,
        title: formData.title || undefined,
        clientName: formData.clientName || undefined,
        amount: formData.amount ? parseFloat(formData.amount) : undefined,
        issueDate: formData.issueDate || undefined,
        dueDate: formData.dueDate || undefined,
        notes: formData.notes || undefined
      }

      const result = await uploadedInvoiceService.uploadInvoice(user.id, uploadData)

      if (result.success) {
        showSuccess(
          'Invoice Uploaded',
          `${selectedFile.name} has been uploaded successfully.`
        )
        onUploadSuccess()
        handleClose()
      } else {
        showError('Upload Failed', result.error || 'Failed to upload invoice.')
      }
    } catch (error) {
      console.error('Upload error:', error)
      showError('Upload Failed', 'An unexpected error occurred.')
    } finally {
      setIsUploading(false)
    }
  }

  const handleClose = () => {
    setSelectedFile(null)
    setFormData({
      title: '',
      clientName: '',
      amount: '',
      issueDate: '',
      dueDate: '',
      notes: ''
    })
    setDragActive(false)
    onClose()
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const getFileIcon = (fileType: string) => {
    if (fileType === 'application/pdf') {
      return <DocumentTextIcon className="w-8 h-8 text-red-400" />
    }
    return <DocumentTextIcon className="w-8 h-8 text-blue-400" />
  }

  if (!isOpen) return null

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          onClick={handleClose}
        />

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto glass-mockup rounded-2xl"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-white/10">
            <div>
              <h2 className="text-xl font-semibold text-white">Upload Previous Invoice</h2>
              <p className="text-gray-400 text-sm mt-1">
                Upload invoices from before you signed up to ShootSuite
              </p>
            </div>
            <button
              onClick={handleClose}
              className="p-2 text-gray-400 hover:text-white transition-colors rounded-lg hover:bg-white/10"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          {/* Plan Limit Warning */}
          {planLimits && !planLimits.hasUnlimitedUploads && (
            <div className="p-4 mx-6 mt-4 rounded-xl bg-orange-500/10 border border-orange-500/30">
              <div className="flex items-center space-x-2 text-orange-400 mb-2">
                <ExclamationTriangleIcon className="w-5 h-5" />
                <span className="font-medium">Upload Limit</span>
              </div>
              <p className="text-orange-300 text-sm">
                You can upload {planLimits.maxUploadedInvoices - planLimits.currentUploadedCount} more invoices 
                ({planLimits.currentUploadedCount}/{planLimits.maxUploadedInvoices} used).
                <br />
                <span className="text-orange-200">Upgrade to Professional for unlimited uploads.</span>
              </p>
            </div>
          )}

          <div className="p-6 space-y-6">
            {/* File Upload Area */}
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-3">
                Invoice File *
              </label>
              
              {!selectedFile ? (
                <div
                  className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                    dragActive
                      ? 'border-blue-400 bg-blue-500/10'
                      : 'border-white/20 hover:border-white/40'
                  }`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                >
                  <CloudArrowUpIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-white font-medium mb-2">
                    Drop your invoice file here, or click to browse
                  </p>
                  <p className="text-gray-400 text-sm mb-4">
                    Supports PDF, JPG, PNG, GIF, WebP (max 50MB)
                  </p>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="btn-secondary"
                  >
                    Choose File
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,.gif,.webp"
                    onChange={handleFileInputChange}
                    className="hidden"
                  />
                </div>
              ) : (
                <div className="glass-mockup rounded-xl p-4">
                  <div className="flex items-center space-x-4">
                    {getFileIcon(selectedFile.type)}
                    <div className="flex-1">
                      <p className="text-white font-medium">{selectedFile.name}</p>
                      <p className="text-gray-400 text-sm">{formatFileSize(selectedFile.size)}</p>
                    </div>
                    <button
                      onClick={() => setSelectedFile(null)}
                      className="p-2 text-gray-400 hover:text-white transition-colors rounded-lg hover:bg-white/10"
                    >
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Metadata Form */}
            {selectedFile && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-white">Invoice Details (Optional)</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">
                      Invoice Title
                    </label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => handleInputChange('title', e.target.value)}
                      placeholder="e.g., Wedding Photography Invoice"
                      className="input-field"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">
                      Client Name
                    </label>
                    <div className="relative">
                      <UserIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="text"
                        value={formData.clientName}
                        onChange={(e) => handleInputChange('clientName', e.target.value)}
                        placeholder="Client name"
                        className="input-field pl-10"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">
                      Amount
                    </label>
                    <div className="relative">
                      <CurrencyDollarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="number"
                        value={formData.amount}
                        onChange={(e) => handleInputChange('amount', e.target.value)}
                        placeholder="0.00"
                        step="0.01"
                        min="0"
                        className="input-field pl-10"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">
                      Issue Date
                    </label>
                    <div className="relative">
                      <CalendarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="date"
                        value={formData.issueDate}
                        onChange={(e) => handleInputChange('issueDate', e.target.value)}
                        className="input-field pl-10"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">
                      Due Date
                    </label>
                    <div className="relative">
                      <CalendarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="date"
                        value={formData.dueDate}
                        onChange={(e) => handleInputChange('dueDate', e.target.value)}
                        className="input-field pl-10"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Notes
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => handleInputChange('notes', e.target.value)}
                    placeholder="Additional notes about this invoice..."
                    rows={3}
                    className="input-field resize-none"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end space-x-3 p-6 border-t border-white/10">
            <button
              onClick={handleClose}
              className="btn-secondary"
              disabled={isUploading}
            >
              Cancel
            </button>
            <button
              onClick={handleUpload}
              disabled={!selectedFile || isUploading || (planLimits ? !planLimits.canUploadInvoice : false)}
              className="btn-primary"
            >
              {isUploading ? 'Uploading...' : 'Upload Invoice'}
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  )
}

export default InvoiceUploadModal 